import { FONT_CATALOG, isArtFormat, formatRatios, validateDocument } from '../features/ArtEditor/headless';
import { ART_CATEGORIES, artCategories, bindingsOf, sampleBrandKit, summaryOf, templateDocument, templateEntries, templateEntry } from './artSamples';
import { COMMUNITY_CATEGORIES, COMMUNITY_IMAGES, communityCategoryView, communityImageView, rankByWords } from './communityImages';
import { IMAGE_CEILINGS, IMAGE_COSTS, IMAGE_MINIMUMS, IMAGE_PLACE_LIMIT, IMAGE_QUESTION_TTL_MS, IMAGE_SOURCES, imageNotes, imagePlaces, imageWords, placeImage, sampleImage } from './artImages';

// The art part of the in-memory core (contract 2.7.0, data/studio.openapi.json):
// templates and categories per format (single posts and carousels), art revisions (autosave
// with `revision_conflict`, never while a run works; a carousel's pages are part of the
// document), the profile brand kit, the creator's image gallery (`art_image` assets) and what
// an art export freezes (one page of a version). Runs, quotes, messages and the export
// record stay the shared ones; `succeedArt` plays the worker's part. Asset images are
// stored without URLs and signed on read, as the backend must do. The 2.8.0 proposal
// (data/artTemplates.openapi.json) adds the creator's personal templates (imported or saved from an art).
// Contract 2.9.0 adds images: every gallery image has `origin` and `description` (filtered by
// both, described by the creator), the community gallery (`features.community_images`), and
// the image question: `askImages` parks an art run on the places of its draft that want an
// image (data/artImages.js), the answer is checked and an `image` quote consumed, and
// `placeImages` plays the images phase (generating, when `features.art_image_generation` is on,
// a sample photo saved as `generated`).
const TEMPLATE_ID = /^sat-[0-9a-z]{7,10}$/;
const ASSET_ID = /^sas-[0-9a-z]{7,10}$/;
export const ART_EXPORT_FORMATS = ['png', 'jpeg'];
export const ART_EXPORT_RENDERER = 'studio-art-web-v1';
const ATTACHMENT_LIMIT = 5;
const ATTACHMENT_ROLES = ['auto', 'reference', 'background', 'photo', 'logo'];
const COLOR_ROLES = ['primary', 'secondary', 'accent', 'background', 'text', 'extra'];
export const ART_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
export const ART_IMAGE_MAX_BYTES = 15 * 1024 * 1024;
// 2.13.0: a video for carousel arts, whole (the editor trims it), with its pixel size and length.
export const ART_VIDEO_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];
export const ART_VIDEO_MAX_BYTES = 300 * 1024 * 1024;
const ACTIVE = ['queued', 'running', 'waiting_user'];
const normalize = value => (value || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// A stable stand-in for the SHA-256 the backend computes over the canonical document.
function digest(value) {
  const text = JSON.stringify(value);
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) { hash ^= text.charCodeAt(i); hash = Math.imul(hash, 0x01000193) >>> 0; }
  return hash.toString(16).padStart(8, '0').repeat(8);
}

export function createArtRoutes({ db, fail, nextId, creator, project, workspace, touch, now, stamps, clone, message, settle, reserve }) {
  const locale = () => db().creator?.locale || 'pt_BR';
  const isArt = p => isArtFormat(p?.brief?.format);
  const artProject = projectId => { const p = project(projectId); if (!isArt(p)) fail('not_found', 404); return p; };
  const revisionsOf = p => db().artRevisions.filter(revision => revision.project_id === p.id);
  const findRevision = id => db().artRevisions.find(revision => revision.id === id && revision.creator_id === creator().id) || fail('not_found', 404);
  const assetUrl = id => db().assetUrls[id] || `https://uploads.studio.test/${id}`;
  const errorsOf = list => Object.fromEntries(list.map(error => [error.path, [error.message]]));

  // Private uploads (image layers, and shapes and texts painted with a photo; 2.13.0 videos)
  // are stored by asset id and signed on every read.
  const mapImages = (doc, fn) => ({ ...doc, pages: doc.pages.map(page => ({ ...page, layers: page.layers.map(layer => {
    if ((layer.type === 'image' || layer.type === 'video') && layer.source?.kind === 'asset') return { ...layer, source: fn(layer.source) };
    if (layer.type === 'shape' && layer.fill?.type === 'image' && layer.fill.source?.kind === 'asset') return { ...layer, fill: { ...layer.fill, source: fn(layer.fill.source) } };
    if (layer.type === 'text' && layer.style?.fill?.type === 'image' && layer.style.fill.source?.kind === 'asset') {
      return { ...layer, style: { ...layer.style, fill: { ...layer.style.fill, source: fn(layer.style.fill.source) } } };
    }
    return layer;
  }) })) });
  const signed = doc => mapImages(doc, source => ({ ...source, url: assetUrl(source.asset_id) }));
  const stored = doc => mapImages(doc, source => ({ ...source, url: null }));
  // Revisions stored before versions counted their saves read as a first, unedited save.
  const revisionView = revision => ({ ...revision, version: revision.version || 1, edited: !!revision.edited, document: signed(revision.document) });

  // A project's document must be in the project's format; a template's (`format` null) in any art format.
  const documentErrors = (doc, { format = null, creatorId }) => {
    const errors = validateDocument(doc);
    if (format && doc?.format !== format) errors.push({ path: 'document.format', message: 'must be the project format' });
    (doc?.pages || []).forEach((page, index) => page.layers?.forEach((layer, position) => {
      // 2.13.0: a video is an `art_video` of the creator.
      const [source, at] = layer.type === 'image' || layer.type === 'video' ? [layer.source, 'source'] : layer.type === 'shape' && layer.fill?.type === 'image' ? [layer.fill.source, 'fill.source']
        : layer.type === 'text' && layer.style?.fill?.type === 'image' ? [layer.style.fill.source, 'style.fill.source'] : [null, null];
      if (source?.kind !== 'asset') return;
      const purpose = layer.type === 'video' ? 'art_video' : 'art_image';
      const asset = db().assets.find(item => item.id === source.asset_id && item.creator_id === creatorId && item.purpose === purpose && item.status === 'uploaded');
      if (!asset) errors.push({ path: `document.pages.${index}.layers.${position}.${at}.asset_id`, message: `is not an uploaded ${layer.type === 'video' ? 'video' : 'image'} of this creator` });
    }));
    return errors;
  };
  const checkDocument = (doc, p) => {
    const errors = documentErrors(doc, { format: p.brief.format, creatorId: p.creator_id });
    if (errors.length) fail('validation_error', 422, {}, errorsOf(errors));
  };

  // A version is what an AI run made (`origin: ai`); the creator's edits update it in place
  // (`updateArtRevision`, its `version` counting the saves), they never add one.
  const write = (p, { document, caption = null, template_id, change_summary = null, changed_layer_ids = [], suggestions = [], warnings = [] }, runId) => {
    const base = db().artRevisions.find(revision => revision.id === p.latest_revision_id);
    const revision = { id: nextId('sav'), project_id: p.id, creator_id: p.creator_id, parent_revision_id: p.latest_revision_id, run_id: runId, origin: 'ai',
      revision_number: revisionsOf(p).length + 1, version: 1, edited: false, document: stored(clone(document)), caption: caption ? clone(caption) : base?.caption ? clone(base.caption) : null,
      template_id: template_id === undefined ? base?.template_id ?? null : template_id, change_summary, changed_layer_ids, suggestions, warnings,
      source_digest: digest(stored(document)), prompt_version: 'art-v1.0.0', timestamps: stamps() };
    db().artRevisions.push(revision);
    p.latest_revision_id = revision.id;
    touch(p);
    return revision;
  };

  // The profile's previous arts, offered as templates (`past_design`), most recent first.
  const pastDesigns = profileId => db().projects
    .filter(p => p.profile_id === profileId && p.status === 'active' && isArt(p) && p.latest_revision_id)
    .map(p => ({ p, revision: db().artRevisions.find(revision => revision.id === p.latest_revision_id) }))
    .filter(item => item.revision)
    .sort((a, b) => Date.parse(b.revision.timestamps.created) - Date.parse(a.revision.timestamps.created));
  const pastId = revision => `sat-${revision.id.slice(4)}`;
  const pastSummary = ({ p, revision }) => ({ id: pastId(revision), name: p.title, description: null, format: revision.document.format, aspect_ratio: revision.document.aspect_ratio,
    category_key: templateEntry(revision.template_id)?.category || personalRow(revision.template_id)?.category_key || 'tips', source: 'past_design', author: null, page_count: revision.document.pages.length, page_roles: null, tags: [], previews: [],
    origin: { project_id: p.id, art_revision_id: revision.id }, compatibility: { score: 0.9, reasons: ['same_format', 'recent'] },
    timestamps: { created: revision.timestamps.created, updated: revision.timestamps.updated } });
  const pastById = id => {
    const revision = db().artRevisions.find(item => `sat-${item.id.slice(4)}` === id && item.creator_id === db().creator?.id);
    const p = revision && db().projects.find(item => item.id === revision.project_id);
    return revision && p ? { p, revision } : null;
  };
  // The creator's personal templates (2.8.0 proposal), imported from a JSON file or saved from
  // an art, for the whole account. A deleted one stays as a row (`deleted`), so the arts made
  // with it keep their `template_id`, but nothing reads or picks it any more.
  const personal = () => db().artTemplates || (db().artTemplates = []);
  const personalRow = id => personal().find(item => item.id === id && item.creator_id === db().creator?.id) || null;
  const personalById = id => { const item = personalRow(id); return item && !item.deleted ? item : null; };
  const personalSummary = item => ({ id: item.id, name: item.name, description: item.description, format: item.document.format, aspect_ratio: item.document.aspect_ratio,
    category_key: item.category_key, source: 'personal', author: null, page_count: item.document.pages.length, page_roles: null, tags: clone(item.tags), previews: [], origin: item.origin ? clone(item.origin) : null,
    compatibility: null, timestamps: clone(item.timestamps) });
  const personalTemplate = item => { const document = signed(item.document); return { ...personalSummary(item), document, bindings: bindingsOf(document) }; };
  const personalOr404 = id => personalById(id) || fail('not_found', 404);
  const templateExists = id => !!templateEntry(id) || !!pastById(id) || !!personalById(id);
  // Name, description, category and tags, as a create or an update sends them.
  const templateFieldErrors = ({ name, description, category_key: categoryKey, tags }, { partial = false } = {}) => {
    const errors = {};
    const given = value => !partial || value !== undefined;
    if (given(name) && (typeof name !== 'string' || !name.trim() || name.trim().length > 80)) errors.name = ['1 to 80 characters.'];
    if (description !== undefined && description !== null && (typeof description !== 'string' || description.length > 280)) errors.description = ['Up to 280 characters.'];
    if (given(categoryKey) && !ART_CATEGORIES.some(category => category.key === categoryKey)) errors.category_key = ['Not a gallery category.'];
    if (tags !== undefined && (!Array.isArray(tags) || tags.length > 10 || tags.some(tag => typeof tag !== 'string' || !tag.length || tag.length > 40))) errors.tags = ['Up to 10 tags of 1 to 40 characters.'];
    return errors;
  };

  const brandKitRow = profile => db().brandKits[profile.id] || (db().brandKits[profile.id] = { id: nextId('sbk'), profile_id: profile.id, ...sampleBrandKit(), logo_asset_id: null,
    version: 1, timestamps: { created: now(), updated: now() } });
  // 2.10.0: every part of a text style (null = the template's), the subheading (null = the heading's).
  const styleView = font => ({ family: font.family, weight: font.weight, italic: font.italic ?? null, transform: font.transform ?? null,
    size: font.size ?? null, line_height: font.line_height ?? null, letter_spacing: font.letter_spacing ?? null });
  // 2.10.0: the profile's Instagram picture as the arts' avatar (core copies it into an `art_image` of origin `profile`).
  const avatarOf = profileId => {
    const profile = db().creator?.profiles?.find(item => item.id === profileId);
    return profile?.picture?.url ? { asset_id: `sas-avtr${profileId.slice(4, 10)}`, url: profile.picture.url, width: 400, height: 400 } : null;
  };
  const brandKitView = ({ logo, logo_asset_id: logoId, ...kit }) => {
    const asset = logoId && db().assets.find(item => item.id === logoId);
    const fonts = { heading: styleView(kit.fonts.heading), subheading: kit.fonts.subheading ? styleView(kit.fonts.subheading) : null, body: styleView(kit.fonts.body) };
    return clone({ ...kit, fonts, logo: asset ? { asset_id: asset.id, url: assetUrl(asset.id), width: asset.image.width, height: asset.image.height } : null,
      avatar: avatarOf(kit.profile_id) });
  };

  // ── images (2.9.0) ──
  // A gallery image as read: how it entered Studio, what it shows (null until described), its signed GET.
  const imageView = ({ profile_id: _, ...asset }) => ({ ...asset, origin: asset.origin || 'upload', description: asset.description ?? null, download: { url: assetUrl(asset.id) } });
  // An uploaded `art_image` of the project's profile, with its size: what a choice may place.
  const ownImage = (p, assetId) => db().assets.find(asset => asset.id === assetId && asset.creator_id === p.creator_id && asset.profile_id === p.profile_id
    && asset.purpose === 'art_image' && asset.status === 'uploaded' && asset.image) || null;
  const seedImage = (profileId, { url, width, height, mime_type = 'image/webp', origin = 'upload', description = null }) => {
    const asset = { id: nextId('sas'), creator_id: creator().id, profile_id: profileId, project_id: null, export_id: null, purpose: 'art_image', status: 'uploaded', mime_type,
      byte_size: 240000, sha256: 'b'.repeat(64), upload: null, download: null, image: { width, height }, origin, description,
      timestamps: { ...stamps(), uploaded: now(), upload_expires: null, download_expires: null } };
    db().assets.push(asset);
    db().assetUrls[asset.id] = url;
    return asset;
  };
  const communityOn = () => { if (!db().features.community_images) fail('studio_unavailable', 403); };
  // Each run that asked about images: the draft the question completes (stored like a revision,
  // asset URLs off), its places, the answer and, once the images phase ran, the art it ends on.
  const imageRuns = () => db().artImages || (db().artImages = {});
  // What each `image` quote was given for: its question and its places with their modes.
  const imageQuotes = () => db().imageQuotes || (db().imageQuotes = {});
  const slotKey = places => places.map(place => `${place.slot_id}:${place.mode}`).sort().join(',');
  const imageCharge = runId => `${runId}:image`;
  const localeOf = p => (db().creator?.profiles || []).find(item => item.id === p.profile_id)?.locale || locale();
  const signedSource = source => (source?.kind === 'asset' ? { ...source, url: assetUrl(source.asset_id) } : source);
  // An action as read: an open image question with its URLs signed and the draft in `preview`;
  // `images` and `preview` are null on every other action (and `preview` once it is answered).
  const actionView = action => {
    if (action.kind !== 'images') return { ...action, images: action.images ?? null, preview: action.preview ?? null };
    const record = imageRuns()[action.run_id];
    return { ...action,
      images: action.images.map(image => ({ ...image, current: signedSource(image.current), suggested: image.suggested.map(item => ({ ...item, url: assetUrl(item.asset_id) })) })),
      preview: action.status === 'open' && record ? { document: signed(record.draft.document), caption: record.draft.caption ? clone(record.draft.caption) : null } : null };
  };
  const runView = run => ({ ...run, pending_actions: (run.pending_actions || []).map(actionView) });

  // `POST /projects/{id}/usage-quotes {kind: image}`: the ceiling of the places to generate of the
  // run's open image question, by the mode of each. Returns the quoted units; the quote keeps
  // its question and places, which the answer must match. In core's order: the request's shape
  // (1 to 10 places, a repeated one keyed at each of its indexes, like Laravel's `distinct`),
  // generation offered, an images question of one of the project's runs (else 422 `action_id`)
  // still open on a run waiting for the creator (else 409), then each place of it.
  const imageQuote = (p, { action_id: actionId, slot_ids: slotIds } = {}, quoteId) => {
    const ids = Array.isArray(slotIds) ? slotIds : [];
    const shape = {};
    if (!ids.length || ids.length > IMAGE_PLACE_LIMIT) shape.slot_ids = ['1 to 10 places of the question.'];
    ids.forEach((slotId, index) => { if (ids.indexOf(slotId) !== ids.lastIndexOf(slotId)) shape[`slot_ids.${index}`] = ['Each place once.']; });
    if (Object.keys(shape).length) fail('validation_error', 422, {}, shape);
    if (!db().features.art_image_generation) fail('validation_error', 422, {}, { kind: ['Generating images is not offered.'] });
    // An answered question leaves the run's `pending_actions`; its record keeps its id.
    const run = db().runs.find(item => item.project_id === p.id && ((item.pending_actions || []).some(action => action.id === actionId) || imageRuns()[item.id]?.action_id === actionId));
    const action = run?.pending_actions.find(item => item.id === actionId) || null;
    if (!run || (action && action.kind !== 'images')) fail('validation_error', 422, {}, { action_id: ['No images question with this id in this project.'] });
    if (!action || action.status !== 'open' || run.status !== 'waiting_user') fail('action_not_open', 409);
    const errors = {};
    ids.forEach((slotId, index) => { if (!action.images.some(image => image.slot_id === slotId)) errors[`slot_ids.${index}`] = ['Not a place of this question.']; });
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    const places = ids.map(slotId => action.images.find(image => image.slot_id === slotId));
    imageQuotes()[quoteId] = { action_id: action.id, slots: slotKey(places) };
    return places.reduce((total, place) => total + IMAGE_CEILINGS[place.mode], 0);
  };

  // The answer to an image question (the shared respond route has checked the action, its
  // version and the response kind). `choose_images` answers every place once; a `generate`
  // consumes the image quote of exactly those places and reserves its ceiling (or the whole
  // balance above the modes' minimum), and a quote goes only with a `generate`. Either answer
  // puts the run back to work on its images. As in core, the request's shape is checked first
  // (the list, each source and subject), then the choices against the question: an unknown or
  // repeated place at `images.N.slot_id`, the image each source names, and only when every
  // choice holds, a place left unanswered (`images`) and the quote (`usage_quote_id`).
  const respondImages = (run, action, body) => {
    const p = db().projects.find(item => item.id === run.project_id);
    let answer = { kind: 'skip', at: now() };
    if (body.kind === 'choose_images') {
      const places = new Map(action.images.map(image => [image.slot_id, image]));
      const list = Array.isArray(body.images) ? body.images : [];
      const shape = {};
      if (!list.length || list.length > IMAGE_PLACE_LIMIT) shape.images = ['1 to 10 choices.'];
      list.forEach((choice, index) => {
        const at = `images.${index}`;
        if (!IMAGE_SOURCES.includes(choice?.source)) shape[`${at}.source`] = ['generate, asset, community or keep.'];
        if (choice?.source === 'generate' && choice.subject != null && (typeof choice.subject !== 'string' || !choice.subject.trim() || choice.subject.length > 300)) shape[`${at}.subject`] = ['1 to 300 characters.'];
      });
      if (Object.keys(shape).length) fail('validation_error', 422, {}, shape);
      const errors = {};
      const seen = new Set();
      const generate = [];
      list.forEach((choice, index) => {
        const at = `images.${index}`;
        if (!places.has(choice.slot_id)) { errors[`${at}.slot_id`] = ['Not a place of this question.']; return; }
        if (seen.has(choice.slot_id)) { errors[`${at}.slot_id`] = ['Each place is answered once.']; return; }
        seen.add(choice.slot_id);
        if (choice.source === 'asset' && !ownImage(p, choice.asset_id)) errors[`${at}.asset_id`] = ['Not an uploaded image of this profile.'];
        if (choice.source === 'community' && !COMMUNITY_IMAGES.some(image => image.id === choice.community_image_id)) errors[`${at}.community_image_id`] = ['Not a community image.'];
        if (choice.source === 'generate') {
          if (!db().features.art_image_generation) errors[`${at}.source`] = ['Generating images is not offered.'];
          generate.push(choice);
        }
      });
      const clean = () => !Object.keys(errors).length;
      if (clean() && [...places.keys()].some(slotId => !seen.has(slotId))) errors.images = ['One choice for every place of the question.'];
      if (clean() && generate.length && !body.usage_quote_id) errors.usage_quote_id = ['Generating needs an image quote.'];
      if (clean() && !generate.length && body.usage_quote_id) errors.usage_quote_id = ['Send the quote only when there are images to generate.'];
      if (!clean()) fail('validation_error', 422, {}, errors);
      if (generate.length) {
        // QuoteRedeemer::consume's order: the project's quote, then consumed, expired, and its kind and places.
        const quote = db().quotes.find(item => item.id === body.usage_quote_id && item.project_id === p.id)
          || fail('validation_error', 422, {}, { usage_quote_id: ['Not a quote of this project.'] });
        if (quote.status === 'consumed') fail('usage_quote_consumed', 409);
        if (quote.status === 'expired' || Date.parse(quote.timestamps.expires) < Date.now()) { quote.status = 'expired'; fail('usage_quote_expired', 410); }
        const target = imageQuotes()[quote.id];
        if (quote.kind !== 'image' || target?.action_id !== action.id || target.slots !== slotKey(generate.map(choice => places.get(choice.slot_id)))) fail('usage_quote_mismatch', 409);
        const units = reserve(db(), quote, generate.reduce((total, choice) => total + IMAGE_MINIMUMS[places.get(choice.slot_id).mode], 0));
        quote.status = 'consumed';
        db().charges[imageCharge(run.id)] = units;
      }
      answer = { kind: 'choose_images', at: now(), images: list.map(({ slot_id: slotId, source, asset_id: assetId = null, community_image_id: communityId = null, subject = null }) => ({
        slot_id: slotId, source, asset_id: assetId, community_image_id: communityId, subject: source === 'generate' ? subject?.trim() || null : null })) };
    }
    imageRuns()[run.id].answer = answer;
    action.status = 'answered';
    action.timestamps = { ...action.timestamps, updated: now(), answered: now() };
    Object.assign(run, { pending_actions: [], status: 'running', phase: 'images' });
  };

  // The worker's images phase: each choice placed in the draft (an image made for `generate`
  // joins the gallery as `generated`, its subject as its description), the image operation
  // charged what was made, the summary saying what was used. A place that cannot take its image
  // keeps the one it had and warns (`image_generation_failed`). `generate` is the image model:
  // `({ subject, mode, box, placement, locale }) → { url, width, height, mime_type? } | null`.
  const placeImages = (runId, { generate = sampleImage } = {}) => {
    const record = imageRuns()[runId];
    if (!record?.answer) return null;
    if (record.placed) return clone(record.placed);
    const run = db().runs.find(item => item.id === runId);
    const p = db().projects.find(item => item.id === run.project_id);
    const words = imageWords(record.locale);
    let document = clone(record.draft.document);
    const changed = new Set(record.draft.changed_layer_ids || []);
    const warnings = [...(record.draft.warnings || [])];
    const counts = { generated: 0, asset: 0, community: 0 };
    let cost = 0;
    (record.answer.images || []).forEach(choice => {
      const place = record.places.find(item => item.slot_id === choice.slot_id);
      let source = null;
      if (choice.source === 'asset') {
        const asset = ownImage(p, choice.asset_id);
        source = asset && { kind: 'asset', asset_id: asset.id, url: null, width: asset.image.width, height: asset.image.height };
      } else if (choice.source === 'community') {
        const image = COMMUNITY_IMAGES.find(item => item.id === choice.community_image_id);
        source = image && { kind: 'library', asset_id: null, url: image.url, width: image.width, height: image.height };
      } else if (choice.source === 'generate') {
        const subject = choice.subject || place.subject;
        const made = generate({ subject, mode: place.mode, box: place.box, placement: place.placement, locale: record.locale });
        if (made) {
          const asset = seedImage(p.profile_id, { ...made, origin: 'generated', description: subject.slice(0, 500) });
          source = { kind: 'asset', asset_id: asset.id, url: null, width: made.width, height: made.height };
          cost += IMAGE_COSTS[place.mode];
        }
      }
      if (choice.source === 'keep') return;
      const placed = source && placeImage(document, place, source, record.locale);
      if (!placed) { warnings.push({ code: 'image_generation_failed', layer_id: place.layer_id, message: words.failed }); return; }
      document = placed.document;
      placed.changed.forEach(id => changed.add(id));
      counts[choice.source === 'generate' ? 'generated' : choice.source] += 1;
    });
    const held = db().charges[imageCharge(runId)];
    if (held !== undefined) settle(db(), imageCharge(runId), Math.min(cost, held));
    const notes = imageNotes(record.locale, counts);
    const summary = [(record.draft.change_summary || '').replace(/\.$/, ''), ...(notes.length ? notes : [words.kept])].filter(Boolean).join('. ');
    record.placed = { ...record.draft, document, change_summary: `${summary}.`, changed_layer_ids: [...changed], warnings };
    return clone(record.placed);
  };

  const handlers = {
    artTemplateCategories: ({ format } = {}) => {
      creator();
      if (!isArtFormat(format)) fail('validation_error', 422, {}, { format: ['The format is not an art format.'] });
      return { data: artCategories(locale(), format) };
    },
    artTemplates: ({ format, profile_id, category_key, source, q, page: number = 1, per_page: perPage = 30 } = {}) => {
      creator();
      if (!isArtFormat(format)) fail('validation_error', 422, {}, { format: ['The format is not an art format.'] });
      if (!Number.isInteger(perPage) || perPage < 1 || perPage > 100) fail('validation_error', 422, {}, { per_page: ['Between 1 and 100.'] });
      const past = profile_id ? pastDesigns(workspace(profile_id).id).map(pastSummary) : [];
      // The account's personal templates follow the past designs, the most recent first, in every profile.
      const own = personal().filter(item => item.creator_id === creator().id && !item.deleted).slice().reverse().map(personalSummary);
      const words = normalize(q?.trim());
      // Only the format's own: carousel templates and past carousels for `instagram::carousel`.
      const list = [...past, ...own, ...templateEntries().map(entry => summaryOf(entry, locale()))]
        .filter(item => item.format === format)
        .filter(item => !category_key || item.category_key === category_key)
        .filter(item => !source || item.source === source)
        .filter(item => !words || [item.name, item.description, ...item.tags].some(value => normalize(value).includes(words)));
      const last = Math.max(1, Math.ceil(list.length / perPage));
      return { current_page: number, per_page: perPage, total: list.length, last_page: last, data: list.slice((number - 1) * perPage, number * perPage) };
    },
    artTemplate: templateId => {
      creator();
      const entry = templateEntry(templateId);
      if (entry) {
        const document = templateDocument(entry, { locale: locale() });
        return { ...summaryOf(entry, locale()), document, bindings: bindingsOf(document) };
      }
      const own = personalById(templateId);
      if (own) return personalTemplate(own);
      const past = pastById(templateId) || fail('not_found', 404);
      const document = signed(past.revision.document);
      return { ...pastSummary(past), document, bindings: bindingsOf(document) };
    },
    // 2.8.0 proposal: a personal template, for the whole account, in its document's format. An
    // imported file comes with its images already copied to the account by the web; a saved art
    // names its version (`art_revision_id`, the template's `origin`). The document is checked as
    // a save is; its ids stay as they came.
    createArtTemplate: ({ name, description = null, category_key: categoryKey, tags = [], document, art_revision_id: revisionId = null } = {}) => {
      const owner = creator();
      const errors = templateFieldErrors({ name, description, category_key: categoryKey, tags });
      Object.assign(errors, errorsOf(documentErrors(document, { creatorId: owner.id })));
      const revision = revisionId ? db().artRevisions.find(item => item.id === revisionId && item.creator_id === owner.id) : null;
      if (revisionId && !revision) errors.art_revision_id = ['Not an art version of this creator.'];
      if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      const item = { id: nextId('sat'), creator_id: owner.id, name: name.trim(), description: description?.trim() || null, category_key: categoryKey, tags: [...tags],
        origin: revision ? { project_id: revision.project_id, art_revision_id: revision.id } : null, document: stored(clone(document)), deleted: false,
        timestamps: { created: now(), updated: now() } };
      personal().push(item);
      return personalTemplate(item);
    },
    // Rename (and recategorize) a personal template; its document stays.
    updateArtTemplate: (templateId, body = {}) => {
      const item = personalOr404(templateId);
      const errors = templateFieldErrors(body, { partial: true });
      if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      if (body.name !== undefined) item.name = body.name.trim();
      if (body.description !== undefined) item.description = body.description?.trim() || null;
      if (body.category_key !== undefined) item.category_key = body.category_key;
      if (body.tags !== undefined) item.tags = [...body.tags];
      item.timestamps = { ...item.timestamps, updated: now() };
      return personalTemplate(item);
    },
    deleteArtTemplate: templateId => {
      const item = personalOr404(templateId);
      item.deleted = true;
      item.timestamps = { ...item.timestamps, updated: now() };
      return null;
    },
    artRevisions: projectId => {
      const p = artProject(projectId);
      const list = revisionsOf(p).slice().reverse().map(revisionView);
      return { current_page: 1, per_page: 50, total: list.length, last_page: 1, data: list };
    },
    artRevision: revisionId => revisionView(findRevision(revisionId)),
    // Autosave: the latest version takes the creator's edits. An older version, or a stale
    // `expected_version`, conflicts; a working run refuses the save.
    updateArtRevision: (revisionId, body = {}) => {
      const revision = findRevision(revisionId);
      const p = artProject(revision.project_id);
      if (db().runs.some(run => run.project_id === p.id && ACTIVE.includes(run.status))) fail('active_run_exists', 409);
      const latest = db().artRevisions.find(item => item.id === p.latest_revision_id);
      if (revision.id !== p.latest_revision_id || body.expected_version !== (revision.version || 1)) {
        fail('revision_conflict', 409, { current_version: latest.version || 1, current_revision_id: latest.id });
      }
      checkDocument(body.document, p);
      // The template the version already has stays accepted, even a personal one deleted since.
      if (body.template_id && body.template_id !== revision.template_id && !templateExists(body.template_id)) fail('validation_error', 422, {}, { template_id: ['The template was not found.'] });
      Object.assign(revision, { document: stored(clone(body.document)), edited: true, version: (revision.version || 1) + 1, source_digest: digest(stored(body.document)),
        ...(body.caption !== undefined ? { caption: body.caption ? clone(body.caption) : null } : {}), ...(body.template_id !== undefined ? { template_id: body.template_id } : {}) });
      revision.timestamps = { ...revision.timestamps, updated: now() };
      return revisionView(revision);
    },
    brandKit: profileId => brandKitView(brandKitRow(workspace(profileId))),
    updateBrandKit: (profileId, { expected_version, colors, fonts, logo_asset_id, tone } = {}) => {
      const profile = workspace(profileId);
      const kit = brandKitRow(profile);
      if (expected_version !== kit.version) fail('version_conflict', 409, { current_version: kit.version });
      const errors = {};
      if (colors !== undefined && (!Array.isArray(colors) || colors.length > 12 || colors.some(color => !/^#[0-9a-f]{6}$/.test(color.hex) || !COLOR_ROLES.includes(color.role)))) errors.colors = ['Up to 12 colors, #rrggbb with a known role.'];
      if (fonts !== undefined && ['heading', 'body', ...(fonts?.subheading ? ['subheading'] : [])].some(role => !FONT_CATALOG.some(entry => entry.family === fonts?.[role]?.family))) errors.fonts = ['Heading and body must be catalog fonts.'];
      if (logo_asset_id && !db().assets.some(item => item.id === logo_asset_id && item.creator_id === creator().id && item.purpose === 'art_image' && item.status === 'uploaded')) errors.logo_asset_id = ['The logo must be an uploaded image.'];
      if (tone !== undefined && tone !== null && (typeof tone !== 'string' || tone.length > 600)) errors.tone = ['Up to 600 characters.'];
      if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      Object.assign(kit, { configured: true, ...(colors !== undefined ? { colors: clone(colors) } : {}), ...(fonts !== undefined ? { fonts: clone(fonts) } : {}),
        ...(logo_asset_id !== undefined ? { logo_asset_id } : {}), ...(tone !== undefined ? { tone } : {}) });
      kit.version += 1;
      kit.timestamps.updated = now();
      return brandKitView(kit);
    },
    // The creator's gallery: images of arts in the profile, newest first. 2.9.0: `origin` keeps
    // the uploads or the generated ones, `q` a piece of the description (any case or accent).
    // 2.13.0: `purpose=art_video` lists the profile's videos (no `origin` or `q`).
    assets: ({ purpose = 'art_image', profile_id, origin, q, page: number = 1, per_page: perPage = 24 } = {}) => {
      const profile = workspace(profile_id);
      const errors = {};
      const video = purpose === 'art_video';
      if (purpose !== 'art_image' && !video) errors.purpose = ['Only art_image and art_video assets are listed.'];
      if (origin !== undefined && (video || !['upload', 'generated'].includes(origin))) errors.origin = [video ? 'Only for art_image.' : 'upload or generated.'];
      if (q !== undefined && (video || typeof q !== 'string' || !q.length || q.length > 100)) errors.q = [video ? 'Only for art_image.' : '1 to 100 characters.'];
      if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      const words = normalize(q);
      const list = db().assets.filter(asset => asset.purpose === purpose && asset.status === 'uploaded' && asset.profile_id === profile.id && asset.creator_id === creator().id)
        .filter(asset => !origin || (asset.origin || 'upload') === origin)
        .filter(asset => !words || normalize(asset.description).includes(words))
        .slice().reverse().map(imageView);
      const last = Math.max(1, Math.ceil(list.length / perPage));
      return { current_page: number, per_page: perPage, total: list.length, last_page: last, data: list.slice((number - 1) * perPage, number * perPage) };
    },
    // 2.9.0, `PATCH /assets/{id} {description}`: the creator's words for what one of their
    // gallery images shows (an uploaded or a generated one). Another creator's image is a 404.
    describeAsset: (assetId, body = {}) => {
      const asset = db().assets.find(item => item.id === assetId && item.creator_id === creator().id) || fail('not_found', 404);
      const text = typeof body.description === 'string' ? body.description.trim() : '';
      if (Object.keys(body).some(key => key !== 'description') || asset.purpose !== 'art_image' || asset.status !== 'uploaded' || !text || text.length > 500) {
        fail('validation_error', 422, {}, { description: ['1 to 500 characters, on an uploaded art image.'] });
      }
      asset.description = text;
      asset.timestamps = { ...asset.timestamps, updated: now() };
      return imageView(asset);
    },
    // 2.9.0: the community gallery (data/communityImages.js), names and descriptions in the
    // creator's language; 403 `studio_unavailable` while `features.community_images` is off.
    communityImageCategories: () => {
      creator();
      communityOn();
      return { data: COMMUNITY_CATEGORIES.map(category => communityCategoryView(category, locale())).filter(category => category.image_count > 0) };
    },
    // In category order, then the image's. An unknown category is an empty page.
    communityImages: ({ category, q, page: number = 1, per_page: perPage = 24 } = {}) => {
      creator();
      communityOn();
      const errors = {};
      if (category !== undefined && !/^[a-z0-9_]{2,40}$/.test(typeof category === 'string' ? category : '')) errors.category = ['A category key.'];
      if (q !== undefined && (typeof q !== 'string' || !q.length || q.length > 100)) errors.q = ['1 to 100 characters.'];
      if (!Number.isInteger(number) || number < 1) errors.page = ['From 1.'];
      if (!Number.isInteger(perPage) || perPage < 1 || perPage > 50) errors.per_page = ['Between 1 and 50.'];
      if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
      const words = normalize(q);
      const list = COMMUNITY_IMAGES.map(image => communityImageView(image, locale()))
        .filter(image => !category || image.category === category)
        .filter(image => !words || normalize(image.description).includes(words));
      const last = Math.max(1, Math.ceil(list.length / perPage));
      return { current_page: number, per_page: perPage, total: list.length, last_page: last, data: list.slice((number - 1) * perPage, number * perPage) };
    },
  };

  // Rules the shared project and run routes apply to art projects.
  const checkBrief = (brief, profile) => {
    const art = brief.art;
    const errors = {};
    if (!art || typeof art !== 'object') errors['brief.art'] = ['Required for an art format.'];
    else {
      if (!formatRatios(brief.format).includes(art.aspect_ratio)) errors['brief.art.aspect_ratio'] = ['Not offered for this format.'];
      if (art.template_id !== null && art.template_id !== undefined && (!TEMPLATE_ID.test(art.template_id) || !templateExists(art.template_id))) errors['brief.art.template_id'] = ['The template was not found.'];
    }
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
  };
  // Images sent with a prompt or a chat message: at most 5 uploaded `art_image`s of the
  // project's profile, each once, each with a role. In 2.7.0 only art projects take them.
  const checkAttachments = (list, { profile, art: artProject }) => {
    if (list === undefined || list === null) return [];
    const errors = {};
    if (!Array.isArray(list)) errors.attachments = ['Must be a list.'];
    else {
      if (list.length && !artProject) errors.attachments = ['Only art projects take images.'];
      if (list.length > ATTACHMENT_LIMIT) errors.attachments = [`At most ${ATTACHMENT_LIMIT} images.`];
      const seen = new Set();
      list.forEach((item, index) => {
        const uploaded = item && ASSET_ID.test(item.asset_id) && db().assets.some(asset => asset.id === item.asset_id && asset.purpose === 'art_image' && asset.profile_id === profile.id && asset.status === 'uploaded');
        if (!uploaded) errors[`attachments.${index}.asset_id`] = ['Not an uploaded image of this profile.'];
        else if (seen.has(item.asset_id)) errors[`attachments.${index}.asset_id`] = ['Sent twice.'];
        if (item) seen.add(item.asset_id);
        if (!ATTACHMENT_ROLES.includes(item?.role)) errors[`attachments.${index}.role`] = ['Unknown role.'];
      });
    }
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    return list.map(({ asset_id: assetId, role }) => ({ asset_id: assetId, role }));
  };
  // A message as read: its images with a signed GET and their size.
  const messageView = m => (m.attachments ? { ...m, attachments: m.attachments.map(item => {
    const asset = db().assets.find(entry => entry.id === item.asset_id);
    return { asset_id: item.asset_id, role: item.role, url: assetUrl(item.asset_id), width: asset?.image?.width || 1, height: asset?.image?.height || 1 };
  }) } : m);
  const checkRun = (p, body) => {
    if (!isArt(p)) return null;
    if (body.kind === 'revise' && body.base_revision_id !== p.latest_revision_id) fail('revision_conflict', 409, { current_version: p.version, current_revision_id: p.latest_revision_id });
    const base = body.kind === 'revise' ? findRevision(body.base_revision_id) : null;
    const ids = body.target_layer_ids || [];
    const layers = new Set(base ? base.document.pages.flatMap(page => page.layers.map(layer => layer.id)) : []);
    const errors = {};
    ids.forEach((id, index) => { if (!layers.has(id)) errors[`target_layer_ids.${index}`] = ['Not a layer of the base revision.']; });
    if (body.template_id && !templateExists(body.template_id)) errors.template_id = ['The template was not found.'];
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    return { target_layer_ids: ids, template_id: body.template_id || null };
  };
  // An art export: a version of this art project as PNG or JPEG, 1080 (`scale` 1) or 2160 px
  // wide, of one page — `page_id`, a page of that version's document, the first by default (a
  // carousel is exported page by page). The version's whole document is frozen as it is now,
  // so later edits never change the file.
  const freezeExport = (p, { art_revision_id: revisionId, format, scale, page_id: pageId } = {}) => {
    const revision = isArt(p) ? db().artRevisions.find(item => item.id === revisionId && item.project_id === p.id) : null;
    const pages = revision ? revision.document.pages : [];
    const errors = {};
    if (!revision) errors.art_revision_id = [isArt(p) ? 'Not a version of this project.' : 'Only an art project exports an art.'];
    // 2.13.0: `mp4` is one page with a visible video, at 1×.
    const video = format === 'mp4';
    if (!ART_EXPORT_FORMATS.includes(format) && !video) errors.format = ['png, jpeg or mp4.'];
    if (video ? scale !== 1 : ![1, 2].includes(scale)) errors.scale = [video ? '1 (an MP4 is 1080 px wide).' : '1 (1080 px wide) or 2 (2160 px).'];
    if (revision && pageId !== undefined && pageId !== null && !pages.some(item => item.id === pageId)) errors.page_id = ['Not a page of this version.'];
    else if (revision && video && !pages.find(item => item.id === (pageId ?? pages[0].id))?.layers.some(layer => layer.type === 'video' && layer.visible)) errors.page_id = ['An MP4 is a page with a video.'];
    if (Object.keys(errors).length) fail('validation_error', 422, {}, errors);
    return { revision_id: null, art_revision_id: revision.id, format, scale, page_id: pageId ?? pages[0].id, renderer_version: ART_EXPORT_RENDERER,
      document: clone(revision.document), source_digest: revision.source_digest };
  };
  // A frozen art document is signed on every read, like a revision's.
  const exportView = record => (record.art_revision_id ? { ...record, document: signed(record.document) } : record);

  const server = {
    isArt,
    pastDesigns: profileId => pastDesigns(profileId).map(pastSummary),
    pastDocument: templateId => { const past = pastById(templateId); return past ? signed(past.revision.document) : null; },
    personalDocument: templateId => { const item = personalById(templateId); return item ? signed(item.document) : null; },
    revisionDocument: revisionId => { const revision = db().artRevisions.find(item => item.id === revisionId); return revision ? signed(revision.document) : null; },
    brandKit: profileId => { const profile = db().creator?.profiles?.find(item => item.id === profileId); return profile ? brandKitView(brandKitRow(profile)) : null; },
    assetSource: assetId => { const asset = db().assets.find(item => item.id === assetId); return asset?.image ? { kind: 'asset', asset_id: asset.id, url: assetUrl(asset.id), width: asset.image.width, height: asset.image.height } : null; },
    // The worker finishing an art run: a revision with what the AI changed, its summary as
    // the assistant's message, the charge settled. Without a payload, a run that asked about
    // images ends on its draft with the answer's images placed (`placeImages`).
    succeedArt: (runId, payload, cost = 120) => {
      const run = db().runs.find(item => item.id === runId);
      const p = db().projects.find(item => item.id === run.project_id);
      const result = payload || placeImages(runId) || imageRuns()[runId]?.draft;
      const revision = write(p, result, run.id);
      message(p, 'assistant', 'progress', result.change_summary || 'Your art is ready.', { run_id: run.id });
      settle(db(), run.id, cost);
      run.status = 'succeeded'; run.phase = 'done'; run.result_revision_id = revision.id; run.outcome = 'revision'; p.active_run_id = null;
      return clone(touch(run));
    },
    // A creator's image (the mock seeds the gallery with described library photos).
    seedArtImage: (profileId, { mime_type = 'image/jpeg', ...image }) => clone(seedImage(profileId, { mime_type, ...image })),
    // The worker reaching the images phase with the art it designed (`payload`, as `succeedArt`
    // takes it): the places that want an image (template photos of a new layout, and pages
    // without one when the run's message asks for images and brings none, or only the layers
    // of an edit scoped to them) become an open
    // `images` question, with the creator's described images that share words with each
    // subject in `suggested`, and the run waits on it. Every model call is behind the run by
    // then: its reservation keeps `metered` and gives the rest back. null when there is no
    // place: the worker goes on to `succeedArt`.
    askImages: (runId, payload, metered = 120) => {
      const run = db().runs.find(item => item.id === runId);
      const p = db().projects.find(item => item.id === run.project_id);
      const loc = localeOf(p);
      const source = db().messages.find(item => item.id === run.source_message_id);
      const inputs = db().artRunInputs[run.id];
      const newLayout = run.kind === 'generate' || (!!inputs?.template_id && !inputs.target_layer_ids?.length);
      // A scoped edit stays within its scope (ARTS_CONTRACT.md §5): only its layers are places.
      const scope = inputs?.target_layer_ids?.length ? inputs.target_layer_ids : null;
      const places = imagePlaces(payload.document, { prompt: source?.text, newLayout, changed: payload.changed_layer_ids, attached: !!source?.attachments?.length, locale: loc, scope });
      if (!places.length) return null;
      // What the AI would see of the creator's images: described ones of the profile, newest
      // first, without the run's own attachments and the brand kit's logo.
      const skipped = new Set([...(source?.attachments || []).map(item => item.asset_id), db().brandKits[p.profile_id]?.logo_asset_id].filter(Boolean));
      const library = db().assets.filter(asset => asset.description && !skipped.has(asset.id) && ownImage(p, asset.id)).slice().reverse().slice(0, 24)
        .map(asset => ({ text: asset.description, value: asset }));
      const offered = !!db().features.art_image_generation;
      const images = places.map(({ placeholder, ...place }) => {
        const ranked = rankByWords(place.subject, library).slice(0, 3);
        return { ...place, current: place.current?.kind === 'asset' ? { ...place.current, url: null } : place.current,
          suggested: ranked.map(({ value: asset }) => ({ asset_id: asset.id, url: null, width: asset.image.width, height: asset.image.height, description: asset.description })),
          default: ranked[0]?.score >= 2 ? { slot_id: place.slot_id, source: 'asset', asset_id: ranked[0].value.id } : placeholder ? { slot_id: place.slot_id, source: 'keep' } : null,
          generation_credits: offered ? IMAGE_CEILINGS[place.mode] : null };
      });
      const base = db().artRevisions.find(item => item.id === p.latest_revision_id);
      const draft = { ...clone(payload), document: stored(clone(payload.document)), caption: payload.caption ? clone(payload.caption) : base?.caption ? clone(base.caption) : null };
      const action = { id: nextId('sac'), run_id: run.id, kind: 'images', status: 'open', version: 1, prompt: imageWords(loc).question(images.length),
        allowed_responses: ['choose_images', 'skip'], options: [], candidate_memory_ids: [],
        timestamps: { ...stamps(), answered: null, expires: new Date(Date.now() + IMAGE_QUESTION_TTL_MS).toISOString() }, images, preview: null };
      imageRuns()[run.id] = { draft, places: clone(images), action_id: action.id, locale: loc, answer: null, placed: null };
      const held = db().charges[run.id];
      if (held > metered) { db().credits += held - metered; db().charges[run.id] = metered; }
      Object.assign(run, { status: 'waiting_user', phase: 'images', pending_actions: [action] });
      run.timestamps.waiting = now();
      return clone(runView(touch(run)));
    },
    placeImages,
    // When the run's image question was answered and how many images it asked to generate.
    imageAnswer: runId => {
      const answer = imageRuns()[runId]?.answer;
      return answer ? { at: answer.at, kind: answer.kind, generated: (answer.images || []).filter(choice => choice.source === 'generate').length } : null;
    },
    // A cancelled run gives back the image reservation it had not spent.
    releaseImages: runId => { if (db().charges[imageCharge(runId)] !== undefined) settle(db(), imageCharge(runId), 0); },
  };
  return { handlers, checkBrief, checkRun, checkAttachments, messageView, freezeExport, exportView, imageQuote, respondImages, runView, server };
}
